/**
 * Regente.js — Central de Relatórios Inteligentes com IA
 * Estética: Retro-futurista / Sala de controle de missão
 * Refinado com:
 * - modo tela total / janela / minimizado
 * - hero sci-fi com slogan
 * - layout mais imersivo
 * - stepper corrigido
 * - UX premium / cockpit
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';
import './Regente.css';

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════════

const DOMAINS = [
  {
    id: 'financeiro',
    label: 'Controle Financeiro',
    glyph: '◈',
    color: '#00ff9d',
    desc: 'Receitas, despesas, fluxo de caixa e lucratividade',
  },
  {
    id: 'motoristas',
    label: 'Gestão de Motoristas',
    glyph: '⬡',
    color: '#00d4ff',
    desc: 'Produtividade, ranking e performance individual',
  },
  {
    id: 'clientes',
    label: 'Base de Clientes',
    glyph: '◎',
    color: '#ff9500',
    desc: 'Crescimento, segmentação e frequência de uso',
  },
  {
    id: 'ordens',
    label: 'Ordens de Serviço',
    glyph: '▣',
    color: '#a78bfa',
    desc: 'Volume, ticket médio e eficiência operacional',
  },
];

const PERIODOS = [
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: '90d', label: '3 meses' },
  { id: '365d', label: '1 ano' },
];

const VISUALIZACOES = [
  { id: 'cards', glyph: '▣', label: 'KPI Cards', desc: 'Métricas em destaque' },
  { id: 'charts', glyph: '◈', label: 'Gráficos', desc: 'Visualizações dinâmicas' },
  { id: 'table', glyph: '⊞', label: 'Tabelas', desc: 'Dados detalhados' },
  { id: 'mixed', glyph: '◉', label: 'Completo', desc: 'Cards + Gráficos + Tabelas' },
];

const KPIS_BY_DOMAIN = {
  financeiro: [
    'Receita Total',
    'Despesas Totais',
    'Lucro Líquido',
    'Ticket Médio',
    'Evolução Temporal',
    'Despesas por Categoria',
    'Contas a Pagar',
    'Saldo Final',
  ],
  motoristas: [
    'Total de OS',
    'Taxa de Conclusão',
    'Ranking de Produtividade',
    'Tempo Médio por OS',
    'Evolução de OS',
    'Motoristas Ativos vs Inativos',
  ],
  clientes: [
    'Total de Clientes',
    'Novos no Período',
    'Clientes Ativos',
    'Top 10 Clientes',
    'Crescimento Mensal',
    'Frequência de Serviço',
  ],
  ordens: [
    'Total de OS',
    'OS Concluídas',
    'Em Andamento',
    'Canceladas',
    'Receita Total',
    'Ticket Médio',
    'OS por Tipo de Serviço',
    'Evolução Diária',
  ],
};

const AGENT_MSGS = {
  idle: 'SISTEMAS ONLINE. Pronto para gerar inteligência de negócio. Selecione uma operação.',
  wizard_1: 'OPERAÇÃO INICIADA. Selecione o domínio de dados. Cada módulo possui indicadores exclusivos.',
  wizard_2: 'CONFIGURANDO PARÂMETROS. Defina período, visualização e KPIs alvo para máxima precisão.',
  wizard_3: 'COLETANDO BRIEFING. Descreva com detalhe o que precisa ver. Quanto mais contexto, melhor o output.',
  generating: 'PROCESSANDO... Conectando ao banco de dados e consultando Gemini AI. Aguarde.',
  done: 'RELATÓRIO GERADO COM SUCESSO. Visualize, ajuste com feedback ou exporte como HTML standalone.',
  error: 'FALHA NA OPERAÇÃO. Verifique conexão com o serviço de IA e tente novamente.',
};

const PHASES = [
  { label: 'Conectando ao banco de dados', sub: 'Buscando dados reais do seu tenant...' },
  { label: 'Processando métricas e KPIs', sub: 'Calculando indicadores e variações...' },
  { label: 'Consultando Gemini AI', sub: 'Gerando HTML/CSS com inteligência artificial...' },
  { label: 'Finalizando relatório', sub: 'Aplicando design e formatação premium...' },
];

// ══════════════════════════════════════════════════════════════════════════════
// UTILS
// ══════════════════════════════════════════════════════════════════════════════

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// ══════════════════════════════════════════════════════════════════════════════
// ROBOT AVATAR
// ══════════════════════════════════════════════════════════════════════════════
function RoboAvatar({ status }) {
  const c = {
    idle: '#00d4ff',
    thinking: '#a78bfa',
    generating: '#00ff9d',
    done: '#00ff9d',
    error: '#ff3366',
  }[status] || '#00d4ff';

  return (
    <div className={`rg-avatar rg-avatar--${status}`}>
      <svg viewBox="0 0 100 110" xmlns="http://www.w3.org/2000/svg" className="rg-avatar__svg">
        <circle
          cx="50"
          cy="52"
          r="44"
          fill="none"
          stroke={c}
          strokeWidth="1"
          strokeDasharray="8 4"
          opacity="0.4"
          className="rg-ring"
        />
        <polygon points="50,10 85,29 85,69 50,88 15,69 15,29" fill="#0b1629" stroke={c} strokeWidth="1.5" />
        <polygon
          points="50,20 77,35 77,65 50,80 23,65 23,35"
          fill="#060c1a"
          stroke={c}
          strokeWidth="0.5"
          opacity="0.5"
        />
        <rect x="28" y="36" width="16" height="10" rx="3" fill={c} className="rg-eye" />
        <rect x="30" y="38" width="4" height="4" rx="1" fill="#060c1a" />
        <rect x="56" y="36" width="16" height="10" rx="3" fill={c} className="rg-eye" />
        <rect x="58" y="38" width="4" height="4" rx="1" fill="#060c1a" />
        <rect x="32" y="56" width="36" height="5" rx="2.5" fill={c} opacity="0.8" className="rg-mouth" />
        <circle cx="38" cy="68" r="2" fill={c} opacity="0.5" />
        <circle cx="50" cy="70" r="2" fill={c} opacity="0.5" />
        <circle cx="62" cy="68" r="2" fill={c} opacity="0.5" />
        <line x1="50" y1="10" x2="50" y2="2" stroke={c} strokeWidth="2" />
        <circle cx="50" cy="1" r="3.5" fill={c} className="rg-antenna" />
        <circle cx="50" cy="98" r="4" fill={c} className="rg-status-dot" />
        <circle
          cx="50"
          cy="98"
          r="8"
          fill="none"
          stroke={c}
          strokeWidth="1"
          opacity="0.3"
          className="rg-status-ring"
        />
      </svg>
      <div className="rg-avatar__id" style={{ color: c }}>
        REGENTE · AI CORE
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TYPEWRITER MESSAGE
// ══════════════════════════════════════════════════════════════════════════════
function AgentMsg({ msg, status }) {
  const [shown, setShown] = useState('');
  const [i, setI] = useState(0);

  useEffect(() => {
    setShown('');
    setI(0);
  }, [msg]);

  useEffect(() => {
    if (i >= msg.length) return;
    const t = setTimeout(() => {
      setShown((p) => p + msg[i]);
      setI((p) => p + 1);
    }, 12);
    return () => clearTimeout(t);
  }, [i, msg]);

  return (
    <div className={`rg-msg rg-msg--${status}`}>
      <span className="rg-msg__prompt">▶</span>
      <span className="rg-msg__text">{shown}</span>
      {i < msg.length && <span className="rg-msg__caret">█</span>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HERO HEADER
// ══════════════════════════════════════════════════════════════════════════════
function MissionHero({ onNew, reportsCount, userName }) {
  return (
    <section className="rg-hero">
      <div className="rg-hero__noise" />
      <div className="rg-hero__grid" />
      <div className="rg-hero__scanline" />
      <div className="rg-hero__radar" />
      <div className="rg-hero__content">
        <div className="rg-hero__eyebrow">NÚCLEO DE ORQUESTRAÇÃO ANALÍTICA</div>

        <h1 className="rg-hero__title">
          <span>REGENTE</span>
          <small>orquestrando dados. materializando decisões.</small>
        </h1>

        <p className="rg-hero__desc">
          Console de inteligência operacional com geração de relatórios orientados por IA,
          leitura contextual de métricas e visualização premium para operação, gestão e diretoria.
        </p>

        <div className="rg-hero__actions">
          <button className="rg-btn rg-btn--cta rg-btn--hero" onClick={onNew}>
            ⬡ Iniciar Nova Missão
          </button>
          <div className="rg-hero__meta">
            <span>
              <strong>{reportsCount || 0}</strong> relatórios indexados
            </span>
            <span>
              operador: <strong>{userName || 'Usuário'}</strong>
            </span>
          </div>
        </div>
      </div>

      <div className="rg-hero__visual">
        <div className="rg-hero-orb">
          <div className="rg-hero-orb__ring rg-hero-orb__ring--1" />
          <div className="rg-hero-orb__ring rg-hero-orb__ring--2" />
          <div className="rg-hero-orb__ring rg-hero-orb__ring--3" />
          <div className="rg-hero-orb__core">
            <span>◉</span>
          </div>
        </div>

        <div className="rg-hero__readouts">
          <div className="rg-readout">
            <label>status</label>
            <strong>ONLINE</strong>
          </div>
          <div className="rg-readout">
            <label>núcleo</label>
            <strong>AI READY</strong>
          </div>
          <div className="rg-readout">
            <label>modo</label>
            <strong>MISSION CONTROL</strong>
          </div>
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// WINDOW CONTROLS
// ══════════════════════════════════════════════════════════════════════════════
function WindowControls({ mode, onWindow, onFull, onMini }) {
  return (
    <div className="rg-windowbar">
      <div className="rg-windowbar__brand">
        <span className="rg-windowbar__dot rg-windowbar__dot--cyan" />
        <span className="rg-windowbar__dot rg-windowbar__dot--green" />
        <span className="rg-windowbar__dot rg-windowbar__dot--purple" />
        <div className="rg-windowbar__title">
          <strong>REGENTE</strong>
          <small>AI Command Surface</small>
        </div>
      </div>

      <div className="rg-windowbar__controls">
        <button
          type="button"
          className={cn('rg-wctl', mode === 'window' && 'rg-wctl--on')}
          onClick={onWindow}
          title="Modo janela"
        >
          ❐
        </button>
        <button
          type="button"
          className={cn('rg-wctl', mode === 'full' && 'rg-wctl--on')}
          onClick={onFull}
          title="Tela total"
        >
          ⛶
        </button>
        <button
          type="button"
          className="rg-wctl rg-wctl--mini"
          onClick={onMini}
          title="Minimizar"
        >
          —
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DOMAIN CARD
// ══════════════════════════════════════════════════════════════════════════════
function DomainCard({ d, selected, onClick }) {
  return (
    <button
      type="button"
      className={`rg-domain-card ${selected ? 'rg-domain-card--on' : ''}`}
      style={{ '--dc': d.color }}
      onClick={onClick}
    >
      <span className="rg-domain-card__glyph">{d.glyph}</span>
      <span className="rg-domain-card__content">
        <span className="rg-domain-card__label">{d.label}</span>
        <span className="rg-domain-card__desc">{d.desc}</span>
      </span>
      {selected && <span className="rg-domain-card__tick">✓</span>}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// REPORT ROW (library sidebar)
// ══════════════════════════════════════════════════════════════════════════════
function ReportRow({ r, active, onClick }) {
  const d = DOMAINS.find((x) => x.id === r.dominio) || DOMAINS[0];
  return (
    <button type="button" className={`rg-rrow ${active ? 'rg-rrow--active' : ''}`} onClick={onClick}>
      <span className="rg-rrow__glyph" style={{ color: d.color }}>
        {d.glyph}
      </span>
      <span className="rg-rrow__title">{r.titulo}</span>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// REPORT CARD (dashboard grid)
// ══════════════════════════════════════════════════════════════════════════════
function ReportCard({ r, onOpen, onDelete, onFav }) {
  const d = DOMAINS.find((x) => x.id === r.dominio) || DOMAINS[0];

  return (
    <div className="rg-card" style={{ '--dc': d.color }}>
      <div className="rg-card__top">
        <span className="rg-card__glyph">{d.glyph}</span>

        <div className="rg-card__info">
          <span className="rg-card__title">{r.titulo}</span>
          <span className="rg-card__domain" style={{ color: d.color }}>
            {d.label}
          </span>
        </div>

        <button
          type="button"
          className={`rg-fav ${r.is_favorito ? 'rg-fav--on' : ''}`}
          onClick={() => onFav(r.id)}
          title={r.is_favorito ? 'Remover favorito' : 'Favoritar'}
        >
          {r.is_favorito ? '★' : '☆'}
        </button>
      </div>

      <p className="rg-card__desc">
        {r.descricao?.slice(0, 110)}
        {r.descricao?.length > 110 ? '...' : ''}
      </p>

      <div className="rg-card__meta">
        <span>{PERIODOS.find((p) => p.id === r.periodo)?.label || r.periodo}</span>
        <span>v{r.versao}</span>
        <span>{new Date(r.updated_at).toLocaleDateString('pt-BR')}</span>
      </div>

      <div className="rg-card__btns">
        <button type="button" className="rg-btn rg-btn--sm rg-btn--cyan" onClick={() => onOpen(r)}>
          Abrir
        </button>
        <button type="button" className="rg-btn rg-btn--sm rg-btn--ghost" onClick={() => onDelete(r.id)}>
          Excluir
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// GENERATING SCREEN
// ══════════════════════════════════════════════════════════════════════════════
function GeneratingScreen({ phase }) {
  return (
    <div className="rg-gen">
      <div className="rg-gen__backdrop">
        <div className="rg-gen__beam rg-gen__beam--1" />
        <div className="rg-gen__beam rg-gen__beam--2" />
      </div>

      <div className="rg-gen__orb">
        <div className="rg-gen__orb-ring rg-gen__orb-ring--1" />
        <div className="rg-gen__orb-ring rg-gen__orb-ring--2" />
        <div className="rg-gen__orb-ring rg-gen__orb-ring--3" />
        <span className="rg-gen__orb-glyph">⬡</span>
      </div>

      <div className="rg-gen__phases">
        {PHASES.map((p, i) => (
          <div
            key={i}
            className={`rg-phase rg-phase--${i < phase ? 'done' : i === phase ? 'active' : 'wait'}`}
          >
            <span className="rg-phase__bullet">{i < phase ? '✓' : i === phase ? '◈' : '○'}</span>
            <div>
              <div className="rg-phase__label">{p.label}</div>
              {i === phase && <div className="rg-phase__sub">{p.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STAT TILE
// ══════════════════════════════════════════════════════════════════════════════
function StatTile({ val, label, color }) {
  return (
    <div className="rg-stat" style={{ '--sc': color }}>
      <span className="rg-stat__val">{val ?? '—'}</span>
      <span className="rg-stat__label">{label}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MINI DOCK
// ══════════════════════════════════════════════════════════════════════════════
function MiniDock({ onRestore, stats, currentTitle }) {
  return (
    <div className="rg-mini">
      <div className="rg-mini__pulse" />
      <button type="button" className="rg-mini__main" onClick={onRestore}>
        <span className="rg-mini__glyph">⬡</span>
        <span className="rg-mini__txt">
          <strong>REGENTE</strong>
          <small>{currentTitle || 'AI Command Surface minimizado'}</small>
        </span>
      </button>

      <div className="rg-mini__stats">
        <span>{stats?.total_relatorios ?? 0} relatórios</span>
        <span>{stats?.total_geracoes ?? 0} gerações</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function Regente({ user }) {
  // window modes: 'full' | 'window' | 'mini'
  const [windowMode, setWindowMode] = useState('full');

  // views: 'dash' | 'wizard' | 'gen' | 'preview'
  const [view, setView] = useState('dash');
  const [step, setStep] = useState(1);
  const [agSt, setAgSt] = useState('idle');
  const [agMsg, setAgMsg] = useState(AGENT_MSGS.idle);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(0);
  const [html, setHtml] = useState('');
  const [curId, setCurId] = useState(null);
  const [showFb, setShowFb] = useState(false);
  const [fbText, setFbText] = useState('');
  const [search, setSearch] = useState('');
  const [filterDom, setFilterDom] = useState('');
  const [err, setErr] = useState('');

  const [form, setForm] = useState({
    domain: '',
    title: '',
    description: '',
    periodo: '30d',
    kpis: [],
    visualization: 'mixed',
  });

  const phaseRef = useRef(null);

  // ─── data load ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const [rResp, sResp] = await Promise.all([
        api.get('/api/embed/reports'),
        api.get('/api/embed/stats'),
      ]);
      setReports(Array.isArray(rResp.data) ? rResp.data : []);
      setStats(sResp.data || {});
    } catch {
      // silencioso por enquanto
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    return () => {
      if (phaseRef.current) clearInterval(phaseRef.current);
    };
  }, []);

  // teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && windowMode === 'full') {
        setWindowMode('window');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [windowMode]);

  // filtered list
  const filtered = useMemo(() => {
    return reports.filter((r) => {
      const md = !filterDom || r.dominio === filterDom;
      const ms = !search || r.titulo?.toLowerCase().includes(search.toLowerCase());
      return md && ms;
    });
  }, [reports, filterDom, search]);

  // ─── wizard ────────────────────────────────────────────────────────────────
  const startWizard = () => {
    setForm({
      domain: '',
      title: '',
      description: '',
      periodo: '30d',
      kpis: [],
      visualization: 'mixed',
    });
    setStep(1);
    setErr('');
    setView('wizard');
    setAgSt('thinking');
    setAgMsg(AGENT_MSGS.wizard_1);
    if (windowMode === 'mini') setWindowMode('full');
  };

  const next = () => {
    if (step === 1 && !form.domain) {
      setErr('Selecione um domínio.');
      return;
    }
    if (step === 2 && !form.title) {
      setErr('Informe um título.');
      return;
    }

    setErr('');
    const ns = step + 1;
    setStep(ns);
    setAgMsg(AGENT_MSGS[`wizard_${ns}`] || AGENT_MSGS.idle);
  };

  const back = () => {
    if (step === 1) {
      setView('dash');
      setAgSt('idle');
      setAgMsg(AGENT_MSGS.idle);
      return;
    }

    const ps = step - 1;
    setStep(ps);
    setAgMsg(AGENT_MSGS[`wizard_${ps}`] || AGENT_MSGS.idle);
    setErr('');
  };

  const toggleKpi = (kpi) =>
    setForm((p) => ({
      ...p,
      kpis: p.kpis.includes(kpi) ? p.kpis.filter((k) => k !== kpi) : [...p.kpis, kpi],
    }));

  // ─── generate ──────────────────────────────────────────────────────────────
  const generate = async (fbOverride = null) => {
    if (!form.description && !fbOverride) {
      setErr('Descreva o relatório.');
      return;
    }

    setErr('');
    setView('gen');
    setAgSt('generating');
    setAgMsg(AGENT_MSGS.generating);
    setPhase(0);

    if (phaseRef.current) clearInterval(phaseRef.current);

    let p = 0;
    phaseRef.current = setInterval(() => {
      p = Math.min(p + 1, 3);
      setPhase(p);
      if (p >= 3 && phaseRef.current) {
        clearInterval(phaseRef.current);
      }
    }, 5000);

    try {
      const payload = {
        domain: form.domain,
        title: form.title || `Relatório ${DOMAINS.find((d) => d.id === form.domain)?.label}`,
        description: form.description,
        periodo: form.periodo,
        kpis: form.kpis,
        visualization: form.visualization,
        user_nome: user?.nome || 'Usuário',
      };

      if (fbOverride) {
        payload.feedback = fbOverride;
        payload.report_id = curId;
      }

      const { data } = await api.post('/api/embed/generate', payload);

      if (phaseRef.current) clearInterval(phaseRef.current);
      setPhase(4);

      setTimeout(() => {
        setHtml(data.html);
        setCurId(data.id);
        setView('preview');
        setAgSt('done');
        setAgMsg(AGENT_MSGS.done);
        setShowFb(false);
        setFbText('');
        load();
      }, 600);
    } catch (e) {
      if (phaseRef.current) clearInterval(phaseRef.current);
      setView(fbOverride ? 'preview' : 'wizard');
      setStep(3);
      setAgSt('error');
      setAgMsg(AGENT_MSGS.error);
      setErr(e?.response?.data?.error || e?.message || 'Erro desconhecido.');
    }
  };

  const regenerate = () => {
    if (!fbText.trim()) {
      setErr('Descreva os ajustes.');
      return;
    }
    generate(fbText);
  };

  // ─── open report ───────────────────────────────────────────────────────────
  const openReport = async (r) => {
    setCurId(r.id);
    setForm({
      domain: r.dominio,
      title: r.titulo,
      description: r.descricao || '',
      periodo: r.periodo,
      kpis: r.kpis || [],
      visualization: r.visualizacao,
    });

    try {
      const { data } = await api.get(`/api/embed/reports/${r.id}`);
      if (data.html_content) {
        setHtml(data.html_content);
        setView('preview');
        setAgSt('done');
        setAgMsg(`"${r.titulo}" carregado. Ajuste, exporte ou regenere.`);
        if (windowMode === 'mini') setWindowMode('full');
      }
    } catch {
      setErr('Falha ao carregar.');
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Excluir este relatório permanentemente?')) return;

    try {
      await api.delete(`/api/embed/reports/${id}`);
      if (curId === id) {
        setView('dash');
        setCurId(null);
        setHtml('');
      }
      load();
    } catch {
      setErr('Falha ao excluir.');
    }
  };

  const toggleFav = async (id) => {
    try {
      await api.post(`/api/embed/reports/${id}/favorite`);
      load();
    } catch {
      // silencioso
    }
  };

  const exportHtml = () => {
    if (!html) return;
    const b = new Blob([html], { type: 'text/html' });
    const u = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = u;
    a.download = `regente_${(form.title || 'relatorio').replace(/[^\w\-]+/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(u);
  };

  // ─── render wizard steps ───────────────────────────────────────────────────
  const renderStep = () => {
    if (step === 1) {
      return (
        <div className="rg-step">
          <div className="rg-step__hd">
            <span className="rg-step__num">01</span>
            <div>
              <h3>Selecione o Domínio</h3>
              <p>Defina qual núcleo operacional será interpretado pelo Regente.</p>
            </div>
          </div>

          <div className="rg-domain-grid">
            {DOMAINS.map((d) => (
              <DomainCard
                key={d.id}
                d={d}
                selected={form.domain === d.id}
                onClick={() => {
                  setForm((p) => ({ ...p, domain: d.id, kpis: [] }));
                  setErr('');
                }}
              />
            ))}
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="rg-step">
          <div className="rg-step__hd">
            <span className="rg-step__num">02</span>
            <div>
              <h3>Configure os Parâmetros</h3>
              <p>Período, visualização e KPIs para calibrar a missão analítica.</p>
            </div>
          </div>

          <label className="rg-lbl">Título do relatório</label>
          <input
            className="rg-inp"
            maxLength={100}
            placeholder="Ex: Desempenho Financeiro — Março 2026"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />

          <label className="rg-lbl">Período de análise</label>
          <div className="rg-pills">
            {PERIODOS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`rg-pill ${form.periodo === p.id ? 'rg-pill--on' : ''}`}
                onClick={() => setForm((pr) => ({ ...pr, periodo: p.id }))}
              >
                {p.label}
              </button>
            ))}
          </div>

          <label className="rg-lbl">Tipo de visualização</label>
          <div className="rg-viz-grid">
            {VISUALIZACOES.map((v) => (
              <button
                key={v.id}
                type="button"
                className={`rg-viz ${form.visualization === v.id ? 'rg-viz--on' : ''}`}
                onClick={() => setForm((p) => ({ ...p, visualization: v.id }))}
              >
                <span className="rg-viz__g">{v.glyph}</span>
                <span className="rg-viz__l">{v.label}</span>
                <span className="rg-viz__d">{v.desc}</span>
              </button>
            ))}
          </div>

          <label className="rg-lbl">
            KPIs desejados <small>(opcional — selecione ou deixe a IA decidir)</small>
          </label>
          <div className="rg-kpi-wrap">
            {(KPIS_BY_DOMAIN[form.domain] || []).map((k) => (
              <button
                key={k}
                type="button"
                className={`rg-kchip ${form.kpis.includes(k) ? 'rg-kchip--on' : ''}`}
                onClick={() => toggleKpi(k)}
              >
                {form.kpis.includes(k) ? '✓ ' : ''}
                {k}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="rg-step">
          <div className="rg-step__hd">
            <span className="rg-step__num">03</span>
            <div>
              <h3>Descreva a Missão</h3>
              <p>Quanto mais contexto, melhor a leitura, interpretação e apresentação da IA.</p>
            </div>
          </div>

          <div className="rg-ta-box">
            <textarea
              className="rg-ta"
              rows={9}
              maxLength={800}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder={`Descreva com precisão o que precisa ver. Inclua:

• Quais métricas são críticas
• Comparações desejadas (ex.: mês atual vs mês anterior)
• Agrupamentos específicos
• Regras visuais (ex.: valores negativos em vermelho)
• Público-alvo (diretoria, operação, gestão)

Exemplo:
"Relatório de performance dos motoristas do último mês com ranking, tempo médio por OS e comparativo com o mês anterior. Motoristas abaixo da média destacados em vermelho."`}
            />
            <div className="rg-ta-ft">
              <span className={`rg-cc ${form.description.length > 700 ? 'rg-cc--warn' : ''}`}>
                {form.description.length}/800
              </span>
              <span className="rg-ta-tip">⬡ mais contexto = melhor relatório</span>
            </div>
          </div>

          <div className="rg-review">
            <div className="rg-review__title">⬡ REVISÃO DE MISSÃO</div>
            <div className="rg-review__grid">
              <div>
                <label>DOMÍNIO</label>
                <span>{DOMAINS.find((d) => d.id === form.domain)?.label}</span>
              </div>
              <div>
                <label>PERÍODO</label>
                <span>{PERIODOS.find((p) => p.id === form.periodo)?.label}</span>
              </div>
              <div>
                <label>VISUAL</label>
                <span>{VISUALIZACOES.find((v) => v.id === form.visualization)?.label}</span>
              </div>
              <div>
                <label>KPIs</label>
                <span>{form.kpis.length ? `${form.kpis.length} selecionados` : 'Automático pela IA'}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // ─── loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="rg-shell rg-shell--boot">
        <div className="rg-workspace rg-workspace--boot">
          <div className="rg-boot">
            <div className="rg-boot__hex">⬡</div>
            <div className="rg-boot__lines">
              {['INICIALIZANDO REGENTE...', 'CARREGANDO MÓDULOS...', 'PRONTO.'].map((l, i) => (
                <span key={i} style={{ animationDelay: `${i * 0.4}s` }}>
                  {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // minimizado
  if (windowMode === 'mini') {
    return (
      <MiniDock
        onRestore={() => setWindowMode('window')}
        stats={stats}
        currentTitle={view === 'preview' ? form.title : null}
      />
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className={cn('rg-shell', `rg-shell--${windowMode}`)}>
      <div className={cn('rg-workspace', `rg-workspace--${windowMode}`)}>
        <WindowControls
          mode={windowMode}
          onWindow={() => setWindowMode('window')}
          onFull={() => setWindowMode('full')}
          onMini={() => setWindowMode('mini')}
        />

        {/* ── AGENT PANEL ─────────────────────────────────────────────────── */}
        <aside className="rg-panel">
          <div className="rg-panel__scan" />
          <div className="rg-panel__aurora" />

          <RoboAvatar status={agSt} />
          <AgentMsg msg={agMsg} status={agSt} />

          <div className="rg-panel__stats">
            <StatTile val={stats.total_relatorios} label="Relatórios" color="#00d4ff" />
            <StatTile val={stats.total_geracoes} label="Gerações" color="#a78bfa" />
            <StatTile
              val={stats.tempo_medio_ms ? `${Math.round(stats.tempo_medio_ms / 1000)}s` : '—'}
              label="Tempo Médio"
              color="#00ff9d"
            />
          </div>

          <div className="rg-panel__nav">
            <button
              type="button"
              className={`rg-nav ${view === 'dash' ? 'rg-nav--on' : ''}`}
              onClick={() => {
                setView('dash');
                setAgSt('idle');
                setAgMsg(AGENT_MSGS.idle);
              }}
            >
              ▣ Painel Geral
            </button>

            <button type="button" className="rg-nav rg-nav--cta" onClick={startWizard}>
              ⬡ Nova Missão
            </button>
          </div>

          <div className="rg-lib">
            <div className="rg-lib__hd">BIBLIOTECA</div>

            <input
              className="rg-search"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="rg-dfilter">
              <button
                type="button"
                className={`rg-pill rg-pill--xs ${!filterDom ? 'rg-pill--on' : ''}`}
                onClick={() => setFilterDom('')}
              >
                ALL
              </button>

              {DOMAINS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={`rg-pill rg-pill--xs ${filterDom === d.id ? 'rg-pill--on' : ''}`}
                  style={{ '--dc': d.color }}
                  onClick={() => setFilterDom(filterDom === d.id ? '' : d.id)}
                  title={d.label}
                >
                  {d.glyph}
                </button>
              ))}
            </div>

            <div className="rg-lib__list">
              {filtered.slice(0, 10).map((r) => (
                <ReportRow key={r.id} r={r} active={curId === r.id} onClick={() => openReport(r)} />
              ))}

              {!filtered.length && <span className="rg-lib__empty">Nenhum relatório</span>}
            </div>
          </div>
        </aside>

        {/* ── MAIN AREA ───────────────────────────────────────────────────── */}
        <main className="rg-main">
          {/* DASHBOARD */}
          {view === 'dash' && (
            <div className="rg-view rg-view--dash">
              <MissionHero
                onNew={startWizard}
                reportsCount={stats?.total_relatorios}
                userName={user?.nome}
              />

              <div className="rg-view__header rg-view__header--secondary">
                <div>
                  <h2 className="rg-view__h2">Centro de Missões</h2>
                  <p className="rg-view__sub">
                    Relatórios inteligentes gerados por IA com acabamento executivo e leitura operacional.
                  </p>
                </div>
                <button type="button" className="rg-btn rg-btn--cta" onClick={startWizard}>
                  ⬡ Nova Missão
                </button>
              </div>

              <div className="rg-ds-stats">
                {DOMAINS.map((d) => (
                  <div key={d.id} className="rg-ds-tile" style={{ '--dc': d.color }}>
                    <span className="rg-ds-tile__g">{d.glyph}</span>
                    <div>
                      <span className="rg-ds-tile__n">{stats[d.id] || 0}</span>
                      <span className="rg-ds-tile__l">{d.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              {filtered.length === 0 ? (
                <div className="rg-empty">
                  <span className="rg-empty__g">⬡</span>
                  <h3>Nenhum relatório criado ainda</h3>
                  <p>Inicie uma missão e o Regente irá construir seu primeiro relatório em segundos.</p>
                  <button type="button" className="rg-btn rg-btn--cta" onClick={startWizard}>
                    Iniciar agora
                  </button>
                </div>
              ) : (
                <div className="rg-grid">
                  {filtered.map((r) => (
                    <ReportCard
                      key={r.id}
                      r={r}
                      onOpen={openReport}
                      onDelete={deleteReport}
                      onFav={toggleFav}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* WIZARD */}
          {view === 'wizard' && (
            <div className="rg-view rg-view--wizard">
              <div className="rg-wiz-progress">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`rg-ps ${s < step ? 'rg-ps--done' : s === step ? 'rg-ps--on' : ''}`}
                  >
                    <span>{s < step ? '✓' : `0${s}`}</span>
                    <label>{['Domínio', 'Parâmetros', 'Briefing'][s - 1]}</label>
                  </div>
                ))}
                <div className="rg-pbar">
                  <div className="rg-pbar__fill" style={{ width: `${((step - 1) / 2) * 100}%` }} />
                </div>
              </div>

              {err && <div className="rg-err">{err}</div>}

              <div className="rg-wiz-body">{renderStep()}</div>

              <div className="rg-wiz-ft">
                <button type="button" className="rg-btn rg-btn--ghost" onClick={back}>
                  {step === 1 ? '✕ Cancelar' : '← Voltar'}
                </button>

                {step < 3 ? (
                  <button type="button" className="rg-btn rg-btn--cyan" onClick={next}>
                    Próximo →
                  </button>
                ) : (
                  <button type="button" className="rg-btn rg-btn--launch" onClick={() => generate()}>
                    ⬡ Gerar Relatório
                  </button>
                )}
              </div>
            </div>
          )}

          {/* GENERATING */}
          {view === 'gen' && (
            <div className="rg-view rg-view--gen">
              <GeneratingScreen phase={phase} />
            </div>
          )}

          {/* PREVIEW */}
          {view === 'preview' && (
            <div className="rg-view rg-view--preview">
              <div className="rg-tb">
                <button type="button" className="rg-btn rg-btn--ghost rg-btn--sm" onClick={() => setView('dash')}>
                  ← Voltar
                </button>

                <span className="rg-tb__title">{form.title}</span>

                <div className="rg-tb__actions">
                  <button
                    type="button"
                    className="rg-btn rg-btn--outline rg-btn--sm"
                    onClick={() => setShowFb((p) => !p)}
                  >
                    🔁 Regenerar
                  </button>
                  <button type="button" className="rg-btn rg-btn--outline rg-btn--sm" onClick={exportHtml}>
                    ↓ HTML
                  </button>
                </div>
              </div>

              {showFb && (
                <div className="rg-fb-bar">
                  <input
                    className="rg-inp"
                    placeholder="Descreva os ajustes... Ex: 'Adicione gráfico de pizza para despesas por categoria'"
                    value={fbText}
                    onChange={(e) => setFbText(e.target.value)}
                  />
                  <button type="button" className="rg-btn rg-btn--launch rg-btn--sm" onClick={regenerate}>
                    ⬡ Aplicar
                  </button>
                  {err && <span className="rg-err-i">{err}</span>}
                </div>
              )}

              <div className="rg-preview-wrap">
                <iframe
                  className="rg-iframe"
                  srcDoc={html}
                  title="Relatório Regente"
                  sandbox="allow-scripts"
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}